import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Card,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  ArrowBack,
  QrCode,
  Share,
  ContentCopy,
  Download
} from '@mui/icons-material';
import QRCode from 'qrcode';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { affiliateService } from '../../services/affiliateService';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AffiliateScreen = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [value, setValue] = useState(1);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [revenueMonth, setRevenueMonth] = useState(dayjs().format('YYYY-MM'));
  const [registrationMonth, setRegistrationMonth] = useState(dayjs().format('YYYY-MM'));
  const [selectedDate, setSelectedDate] = useState(null);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [selectedDateRegistrations, setSelectedDateRegistrations] = useState([]);
  const [selectedDateRevenue, setSelectedDateRevenue] = useState(null);
  const { currentUser } = useAuth();

  // QR Code related state
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [shareSupported, setShareSupported] = useState(false);

  // Data state for real Firestore data
  const [affiliateData, setAffiliateData] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState({});
  const [monthlyRegistrationData, setMonthlyRegistrationData] = useState({});

  // Load affiliate data from Firestore
  const loadAffiliateData = async () => {
    try {
      setDataLoading(true);
      setDataError(null);

      const dataMap = {};
      const startDate = dayjs().subtract(30, 'day');

      // Load data for the past 30 days
      for (let i = 0; i <= 30; i++) {
        const date = startDate.add(i, 'day').format('YYYY-MM-DD');

        // Get registrations and revenue from Firestore
        const [registrations, revenueData] = await Promise.all([
          affiliateService.getRegistrationCountForDate(date),
          affiliateService.getRevenueForDate(date)
        ]);

        // // Generate clicks with correlation to registrations (since we don't track clicks in Firestore yet)
        // const baseClicks = registrations * (Math.floor(Math.random() * 15) + 10); // 10-24 clicks per registration
        // const clicks = baseClicks + Math.floor(Math.random() * 50); // Add some random variance

        dataMap[date] = {
          revenue: revenueData.totalRevenue,
          revenueBreakdown: revenueData,
          registrations: registrations,
          // clicks: clicks > 0 ? clicks : 1, // Avoid division by zero
          // conversionRate: clicks > 0 ? ((registrations / clicks) * 100).toFixed(2) : '0.00'
        };
      }

      setAffiliateData(dataMap);
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      setDataError('Failed to load affiliate data');
    } finally {
      setDataLoading(false);
    }
  };

  // Load monthly revenue data
  const loadMonthlyRevenueData = async (monthString) => {
    if (monthlyRevenueData[monthString]) return; // Already loaded

    try {
      const data = await getDataForMonth(monthString, 'revenue');
      setMonthlyRevenueData(prev => ({
        ...prev,
        [monthString]: data
      }));
    } catch (error) {
      console.error('Error loading monthly revenue data:', error);
    }
  };

  // Load monthly registration data
  const loadMonthlyRegistrationData = async (monthString) => {
    if (monthlyRegistrationData[monthString]) return; // Already loaded

    try {
      const data = await getDataForMonth(monthString, 'registrations');
      setMonthlyRegistrationData(prev => ({
        ...prev,
        [monthString]: data
      }));
    } catch (error) {
      console.error('Error loading monthly registration data:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAffiliateData();
  }, []);

  // Load monthly data when revenue month changes
  useEffect(() => {
    if (revenueMonth) {
      loadMonthlyRevenueData(revenueMonth);
    }
  }, [revenueMonth]);

  // Load monthly data when registration month changes
  useEffect(() => {
    if (registrationMonth) {
      loadMonthlyRegistrationData(registrationMonth);
    }
  }, [registrationMonth]);

  // Helper function to get data for a specific date
  const getDataForSpecificDate = (date) => {
    const dateKey = dayjs(date).format('YYYY-MM-DD');
    return affiliateData[dateKey];
  };

  // Render content for calendar tiles
  const renderTileContent = ({ date, view }, tabType) => {
    if (view !== 'month') return null;

    const data = getDataForSpecificDate(date);
    if (!data) return null;

    const displayValue = tabType === 'revenue' ? `$${data.revenue}` : `${data.registrations}`;
    const colorClass = tabType === 'revenue' ? 'revenue-stat' : 'registration-stat';

    return (
      <div className={`calendar-tile-content ${colorClass}`}>
        {displayValue}
      </div>
    );
  };

  const getDataForMonth = async (monthString, type) => {
    try {
      if (type === 'revenue') {
        // Use the new detailed revenue data from affiliate service
        const monthlyRevenueData = await affiliateService.getRevenueForMonth(monthString);
        const activeMembers = Object.keys(monthlyRevenueData.affiliateBreakdown).length;

        return {
          revenue: monthlyRevenueData.totalRevenue,
          summary: monthlyRevenueData.summary,
          activeMembers: activeMembers,
          daysWithData: monthlyRevenueData.dailyBreakdown.length,
          affiliateBreakdown: monthlyRevenueData.affiliateBreakdown
        };
      } else {
        // Use registration data from affiliate service
        const registrations = await affiliateService.getRegistrationsForMonth(monthString);
        const [year, month] = monthString.split('-');
        const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();

        let totalRegistrations = registrations.length;
        // let totalClicks = 0;
        let daysWithData = 0;

        // Count days with registration data
        for (let day = 1; day <= daysInMonth; day++) {
          const dateKey = `${year}-${month}-${day.toString().padStart(2, '0')}`;
          const data = affiliateData[dateKey];

          if (data && data.registrations > 0) {
            // totalClicks += data.clicks;
            daysWithData++;
          }
        }

        if (daysWithData === 0 && totalRegistrations === 0) return null;

        // const conversionRate = totalClicks > 0 ? ((totalRegistrations / totalClicks) * 100).toFixed(2) : '0.00';

        return {
          registrations: totalRegistrations,
          // clicks: totalClicks,
          // conversionRate: conversionRate,
          daysWithData: Math.max(daysWithData, totalRegistrations > 0 ? 1 : 0)
        };
      }
    } catch (error) {
      console.error('Error getting data for month:', error);
      return null;
    }
  };

  // Check if Web Share API is supported
  useEffect(() => {
    setShareSupported(navigator.share !== undefined);
  }, []);

  useEffect(() => {
    const fetchAffiliateCode = async () => {
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists() && userDoc.data().isAffiliate) {
          setAffiliateCode(userDoc.data().affiliateCode);
        }
      }
    };

    fetchAffiliateCode();
  }, [currentUser]);

  // Generate QR code when affiliate code is available and QR tab is selected
  useEffect(() => {
    if (value === 1 && affiliateCode && !qrCodeDataUrl) {
      generateQRCode();
    }
  }, [value, affiliateCode, qrCodeDataUrl]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Handle calendar tile click to show details
  const handleDateClick = (date) => {
    setSelectedDate(date);

    if (value === 0) {
      // Registrations tab - show registration details
      const registrations = affiliateService.getRegistrationsForDate(date);
      if (registrations.length > 0) {
        setSelectedDateRegistrations(registrations);
        setRegistrationDialogOpen(true);
      }
    }
  };

  const handleCloseRegistrationDialog = () => {
    setRegistrationDialogOpen(false);
    setSelectedDate(null);
    setSelectedDateRegistrations([]);
  };

  const handleCloseRevenueDialog = () => {
    setRevenueDialogOpen(false);
    setSelectedDate(null);
    setSelectedDateRevenue(null);
  };

  // QR Code functions
  const generateQRCode = async () => {
    setQrLoading(true);
    setQrError('');

    try {
      // Create the affiliate link URL
      const baseUrl = process.env.REACT_APP_NETLIFY_SITE_URL || window.location.origin;
      const affiliateLink = `${baseUrl}/?ref=${affiliateCode}`;

      // Generate QR code
      const qrOptions = {
        width: 256,
        margin: 2,
        color: {
          dark: theme.palette.primary.main,
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      };

      const dataUrl = await QRCode.toDataURL(affiliateLink, qrOptions);
      setQrCodeDataUrl(dataUrl);

    } catch (err) {
      console.error('Error generating QR code:', err);
      setQrError('Failed to generate QR code. Please try again.');
    } finally {
      setQrLoading(false);
    }
  };

  const getAffiliateLink = () => {
    const baseUrl = process.env.REACT_APP_NETLIFY_SITE_URL || window.location.origin;
    return `${baseUrl}/?ref=${affiliateCode}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getAffiliateLink());
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = getAffiliateLink();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
    }
  };

  const handleShare = async () => {
    if (shareSupported) {
      try {
        await navigator.share({
          title: 'Join Bettor Advantage',
          text: `Use my referral code ${affiliateCode} to join Bettor Advantage and get better sports betting odds!`,
          url: getAffiliateLink()
        });
      } catch (err) {
        console.error('Error sharing:', err);
        // Fallback to copy link
        handleCopyLink();
      }
    } else {
      // Fallback to copy link
      handleCopyLink();
    }
  };

  const handleDownloadQR = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `bettor-advantage-referral-${affiliateCode}.png`;
      link.href = qrCodeDataUrl;
      link.click();
    }
  };

  const handleCloseCopySuccess = () => {
    setCopySuccess(false);
  };

  return (
    <Box sx={{ width: '100%', py: 2, minHeight: '100vh' }}>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/account')}
        >
          Back
        </Button>
      </Box>
      {affiliateCode && (
        <Typography variant="h6" sx={{ p: 2, textAlign: 'center' }}>
          Your Affiliate Code: {affiliateCode}
        </Typography>
      )}
      <Paper square>
        <Tabs
          value={value}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
          aria-label="affiliate screen tabs"
        >
          <Tab label="Registrations" />
          <Tab label="QR Code & Link ID" />
        </Tabs>
      </Paper>  
      <TabPanel value={value} index={0}>
        {dataLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading affiliate data...
            </Typography>
          </Box>
        ) : dataError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {dataError}
              <Button onClick={loadAffiliateData} sx={{ ml: 2 }}>
                Retry
              </Button>
            </Alert>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={3}
                sx={{
                  height: '50vh',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1
                }}>
                  <Calendar
                    value={new Date(registrationMonth + '-01')}
                    defaultActiveStartDate={new Date()}
                    onActiveStartDateChange={({ activeStartDate }) => {
                      setRegistrationMonth(dayjs(activeStartDate).format('YYYY-MM'));
                    }}
                    onClickDay={handleDateClick}
                    view="month"
                    showNeighboringMonth={false}
                    showNavigation={true}
                    maxDetail="month"
                    minDetail="month"
                    tileClassName="calendar-tile"
                    className="custom-calendar"
                    tileContent={(props) => renderTileContent(props, 'registrations')}
                  />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              {(() => {
                const data = monthlyRegistrationData[registrationMonth];
                const monthName = dayjs(registrationMonth + '-01').format('MMMM YYYY');

                if (!data) {
                  return (
                    <Card elevation={2} sx={{ height: '50vh', p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <CircularProgress sx={{ alignSelf: 'center' }} />
                      <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                        Loading registration data...
                      </Typography>
                    </Card>
                  );
                }

                return (
                  <Card elevation={2} sx={{ height: '50vh', p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
                      Registrations for {monthName}
                    </Typography>
                    {data ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">
                              {data.registrations}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              New Registrations
                            </Typography>
                          </Box>
                        </Grid>
                        {/* <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="secondary">
                              {data.clicks}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Total Clicks
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Chip 
                              label={`Conversion Rate: ${data.conversionRate}%`}
                              color="success"
                              variant="outlined"
                            />
                          </Box>
                        </Grid> */}
                        <Grid item xs={12}>
                          <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Typography variant="caption" color="textSecondary">
                              Based on {data.daysWithData} days of data
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography variant="body1" sx={{ textAlign: 'center', color: 'textSecondary' }}>
                        No registration data available for this month
                      </Typography>
                    )}
                  </Card>
                );
              })()}
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={value} index={1}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {qrError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {qrError}
            </Alert>
          )}

          {qrLoading ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Generating your QR code...
              </Typography>
            </Box>
          ) : (
            <>
              {/* QR Code Display */}
              <Card sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', maxWidth: 300, mx: 'auto' }}>
                {qrCodeDataUrl && (
                  <img
                    src={qrCodeDataUrl}
                    alt="Referral QR Code"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: theme.spacing(1)
                    }}
                  />
                )}
              </Card>

              {/* Affiliate Code Display */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your Referral Code
                </Typography>
                <Chip
                  label={affiliateCode}
                  variant="outlined"
                  size="large"
                  sx={{
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    py: 2,
                    px: 3
                  }}
                />
              </Box>

              {/* Affiliate Link Display */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Referral Link
                </Typography>
                <Card sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      color: 'text.primary'
                    }}
                  >
                    {affiliateCode && getAffiliateLink()}
                  </Typography>
                </Card>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, justifyContent: 'center', mt: 3 }}>
                <Button
                  onClick={handleCopyLink}
                  startIcon={<ContentCopy />}
                  variant="outlined"
                  fullWidth={isMobile}
                  disabled={qrLoading || !affiliateCode}
                >
                  Copy Link
                </Button>

                <Button
                  onClick={handleShare}
                  startIcon={<Share />}
                  variant="outlined"
                  fullWidth={isMobile}
                  disabled={qrLoading || !affiliateCode}
                >
                  Share
                </Button>

                <Button
                  onClick={handleDownloadQR}
                  startIcon={<Download />}
                  variant="contained"
                  fullWidth={isMobile}
                  disabled={qrLoading || !qrCodeDataUrl}
                >
                  Download QR
                </Button>
              </Box>
            </>
          )}
        </Box>
      </TabPanel>

      {/* Revenue Details Dialog */}
      <Dialog
        open={revenueDialogOpen}
        onClose={handleCloseRevenueDialog}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Revenue Breakdown for {selectedDate && dayjs(selectedDate).format('MMMM D, YYYY')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Revenue: ${selectedDateRevenue?.totalRevenue || 0}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseRevenueDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedDateRevenue && (
            <Box>
              {/* Category Summary Cards */}
              <Typography variant="h6" sx={{ mb: 2 }}>Category Summary</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Typography variant="body2">Monthly Membership</Typography>
                    <Typography variant="h6">${selectedDateRevenue.summary['Membership Fees (Monthly)']}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'success.contrastText' }}>
                    <Typography variant="body2">Yearly Membership</Typography>
                    <Typography variant="h6">${selectedDateRevenue.summary['Membership Fees (Yearly)']}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography variant="body2">Right Way Wager</Typography>
                    <Typography variant="h6">${selectedDateRevenue.summary['Right Way Wager Commission']}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Typography variant="body2">10til Commission</Typography>
                    <Typography variant="h6">${selectedDateRevenue.summary['10til Commission']}</Typography>
                  </Card>
                </Grid>
              </Grid>

              {/* Member-by-Member Breakdown */}
              <Typography variant="h6" sx={{ mb: 2 }}>Revenue from Your Referrals</Typography>
              <List>
                {Object.values(selectedDateRevenue.breakdown).map((member, index) => (
                  <React.Fragment key={member.affiliateCode}>
                    <ListItem>
                      <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip
                              label={member.affiliateCode}
                              color="primary"
                              variant="outlined"
                              size="medium"
                            />
                            <Typography variant="h6" color="primary">${member.total}</Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box>
                              {Object.entries(member.categories).map(([category, amount]) => (
                                amount > 0 && (
                                  <Typography
                                    key={category}
                                    variant="caption"
                                    sx={{
                                      color: category.includes('Membership') ? 'success.main' :
                                        category.includes('Right Way') ? 'warning.main' : 'info.main',
                                      fontWeight: 'medium',
                                      display: 'block'
                                    }}
                                  >
                                    {category.replace('Membership Fees (Monthly)', 'Monthly').replace('Membership Fees (Yearly)', 'Yearly').replace('Right Way Wager Commission', 'Right Way Wager').replace('10til Commission', '10til')}: ${amount}
                                  </Typography>
                                )
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < Object.keys(selectedDateRevenue.breakdown).length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevenueDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration Details Dialog */}
      <Dialog
        open={registrationDialogOpen}
        onClose={handleCloseRegistrationDialog}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Registrations for {selectedDate && dayjs(selectedDate).format('MMMM D, YYYY')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedDateRegistrations.length} new registrations
            </Typography>
          </Box>
          <IconButton onClick={handleCloseRegistrationDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {selectedDateRegistrations.map((registration, index) => (
              <React.Fragment key={registration.id}>
                <ListItem>
                  <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <ListItemText
                    primary={
                      <Chip
                        label={registration.affiliateCode}
                        color="primary"
                        variant="outlined"
                        size="medium"
                      />
                    }
                  />
                </ListItem>
                {index < selectedDateRegistrations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegistrationDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseCopySuccess}
        message="Referral link copied to clipboard!"
        action={
          <IconButton size="small" color="inherit" onClick={handleCloseCopySuccess}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default AffiliateScreen;